class CustomRelewise extends HTMLElement {
    async connectedCallback() {
        if (this.rutine && this.endpoint === 'similar-products' || this.endpoint !== 'similar-products') {
            const result = await this.fetchProducts();
            if (result.recommendations && result.recommendations.length > 0) {
                await this.render(result.recommendations)
            } else {
                this.section.remove();
            }
        } else {
            this.section.remove();
        }
    }

    async render(products) {
        const promises = [];

        products.map(product => {
            const handle = product.data[`${Shopify.shop}_ShopifyHandle`].value;
            if (!handle) return;
            promises.push(fetch(`/products/${handle}?view=item`))
        })

        const responses = await Promise.all(promises);
        const bodies = await Promise.all(
            responses.map((response) => response.text()),
        );

        if (this.shouldClear) {
            const children = this.parentElement.querySelectorAll('*:not(custom-relewise)');
            children.forEach(i => i.remove())
        }

        this.insertAdjacentHTML(this.placement || 'beforebegin', bodies.join(''))
    }

    getRecommender() {
        return new window.Relewise.Recommender(
            window.relewiseSettings.datasetId,
            window.relewiseSettings.apiKey,
            {
                serverUrl: window.relewiseSettings.serverUrl
            });
    }

    fetchProducts() {
        const recommender = this.getRecommender();
        switch (this.endpoint) {
            case 'similar-products':
                return recommender.recommendSimilarProducts(this.similarProducts());
            case 'popular-products':
                return recommender.recommendPopularProducts(this.popularProducts());
            case 'products-viewed-after-viewing-product':
                return recommender.recommendProductsViewedAfterViewingProduct(this.productsViewedAfterViewingProduct());
            case 'purchased-with-product':
                return recommender.recommendPurchasedWithProduct(this.purchasedWithProduct());
        }
    }

    similarProducts() {
        return new window.Relewise.SimilarProductsProductBuilder(this.getRelewiseContextSettings)
            .setSelectedProductProperties(this.selectedPropertiesSettings.product)
            .setSelectedVariantProperties(this.selectedPropertiesSettings.variant)
            .setNumberOfRecommendations(this.numberOfRecommendations)
            .allowFillIfNecessaryToReachNumberOfRecommendations(false)
            .setSimilarProductsEvaluationSettings({
                significanceOfSimilaritiesInDisplayName: 0,
                significanceOfSimilarListPrice: 0,
                significanceOfCommonImmediateParentCategories: 0,
                significanceOfCommonParentsParentCategories: 0,
                significanceOfCommonAncestorCategories: 0,
                significanceOfCommonProductDataKeys: 0,
                significanceOfIdenticalProductDataValues: 0,
                significanceOfSimilarSalesPrice: 0,
                significanceOfSimilarBrand: 0,
                significantProductDataFields: [
                    {
                        key: `${window.Shopify.shop}_custom.rutine`,
                        comparer: "Equals",
                        significance: 10
                    }
                ]
            })
            .product({
                productId: this.productId,
                variantId: this.variantId,
            })
            .filters((filter) => {
                filter.addProductDataFilter(`${window.Shopify.shop}_custom.rutine`, (condition) => condition.addContainsCondition(this.rutine))
            })
            .build();
    }

    popularProducts() {
        return new window.Relewise.PopularProductsBuilder(this.getRelewiseContextSettings)
            .setSelectedProductProperties(this.selectedPropertiesSettings.product)
            .setSelectedVariantProperties(this.selectedPropertiesSettings.variant)
            .sinceMinutesAgo(this.sinceMinutesAgo)
            .basedOn(this.basedOn)
            .setNumberOfRecommendations(this.numberOfRecommendations)
            .build();
    }

    purchasedWithProduct() {
        return new window.Relewise.PurchasedWithProductBuilder(this.getRelewiseContextSettings)
            .setSelectedProductProperties(this.selectedPropertiesSettings.product)
            .setSelectedVariantProperties(this.selectedPropertiesSettings.variant)
            .product({
                productId: this.productId,
                variantId: this.variantId,
            })
            .setNumberOfRecommendations(this.numberOfRecommendations)
            .build();
    }

    productsViewedAfterViewingProduct() {
        return new window.Relewise.ProductsViewedAfterViewingProductBuilder(this.getRelewiseContextSettings)
            .setSelectedProductProperties(this.selectedPropertiesSettings.product)
            .setSelectedVariantProperties(this.selectedPropertiesSettings.variant)
            .product({
                productId: this.productId,
                variantId: this.variantId,
            })
            .setNumberOfRecommendations(this.numberOfRecommendations)
            .build();
    }

    get getRelewiseContextSettings() {
        return {
            currency: window.relewiseSettings.contextSettings.currency,
            displayedAtLocation: this.displayedAtLocation,
            language: window.relewiseSettings.contextSettings.language,
            user: window.relewiseSettings.contextSettings.getUser(),
        };
    }

    get selectedPropertiesSettings() {
        return {
            product: {
                displayName: true,
                dataKeys: [`${Shopify.shop}_ShopifyHandle`]
            },
            variant: {
                displayName: true
            }
        }
    }

    get shouldClear() {
        return JSON.parse(this.getAttribute('clear') || 'false')
    }

    get productId() {
        return this.getAttribute('product-id')
    }

    get variantId() {
        return this.getAttribute('variant-id')
    }

    get rutine() {
        return this.getAttribute('rutine')
    }

    get sinceMinutesAgo() {
        return this.getAttribute('since-minutes-ago')
    }

    get displayedAtLocation() {
        return this.getAttribute('displayed-at')
    }

    get basedOn() {
        return this.getAttribute('based-on')
    }

    get numberOfRecommendations() {
        return this.getAttribute('number-of-recommendations') || 8
    }

    get endpoint() {
        return this.getAttribute('endpoint')
    }

    get section() {
        return this.closest('.shopify-section')
    }
}

class RelewiseViewTrack extends HTMLElement {
    connectedCallback() {
        switch (this.page) {
            case 'collection':
                this.tracker().trackProductCategoryView({
                    idPath: [this.id],
                    user: window.relewiseSettings.contextSettings.getUser(),
                });
                break;
            case 'page':
                this.tracker().trackContentView({
                    contentId: this.id,
                    user: window.relewiseSettings.contextSettings.getUser(),
                });
                break;
            case 'product':
                this.tracker().trackProductView({
                    productId: this.id,
                    variantId: this.variant,
                    user: window.relewiseSettings.contextSettings.getUser(),
                });
                break;
        }
    }

    tracker() {
        return new window.Relewise.Tracker(
            window.relewiseSettings.datasetId,
            window.relewiseSettings.apiKey,
            {
                serverUrl: window.relewiseSettings.serverUrl
            },
        );
    }

    get page() {
        return this.getAttribute('page')
    }

    get id() {
        return this.getAttribute('id')
    }
}

if (!window.customElements.get('custom-relewise')) {
    window.customElements.define('custom-relewise', CustomRelewise);
}

if (!window.customElements.get('relewise-view-track')) {
    window.customElements.define('relewise-view-track', RelewiseViewTrack);
}